import { useId, useState, type KeyboardEvent, type ReactNode } from "react";
import { BosDurum } from "../BosDurum";
import { Tablo, type TabloSutunu } from "../Tablo";

export interface GrafikSatiri {
  anahtar: string;
  etiket: string;
  hucreler: readonly string[];
}

interface GrafikCercevesiProps {
  baslik: string;
  ozet: string;
  bos: boolean;
  bosMesaji?: string;
  basliklar: readonly string[];
  satirlar: readonly GrafikSatiri[];
  children: ReactNode;
  aciklama?: ReactNode;
  kucuk?: boolean;
  /**
   * TABLO SATIRI İÇİNDE (dalga 3, Öğrenciler "Bu hafta"). Satırın kendisi
   * zaten tablo; özet `role="img"` adında, değer ve yön görünür metinde
   * (çocuklar). Özet paragrafı + "Tablo olarak göster" + klavye ayrıntısı
   * her satırda tekrarlanınca 14 satırlık liste 390 px'te ~6000 px oluyordu
   * (ölçüldü). Yalnız tek değerli grafiklerde kullanılır.
   */
  yalin?: boolean;
}

const ODAK_SINIFI =
  "min-h-dokunma rounded-alan border border-notr-300 px-3 py-1 text-sm text-notr-800 focus-visible:outline-2 focus-visible:outline-marka-600";

export function GrafikCercevesi({
  baslik,
  ozet,
  bos,
  bosMesaji = "Henüz ölçüm yok",
  basliklar,
  satirlar,
  children,
  aciklama,
  kucuk = false,
  yalin = false,
}: GrafikCercevesiProps) {
  const [tabloAcik, tabloyuAc] = useState(false);
  const [okunan, okumayiDegistir] = useState("");
  const kimlik = useId();
  const sutunlar: TabloSutunu<GrafikSatiri>[] = [
    { baslik: "Kayıt", hucre: (satir) => satir.etiket, mobil: true },
    ...basliklar.map((baslikMetni, sira) => ({
      baslik: baslikMetni,
      mobil: true,
      sayisal: true,
      hucre: (satir: GrafikSatiri) => satir.hucreler[sira] ?? "Ölçülmedi",
    })),
  ];
  function klavyeGezintisi(olay: KeyboardEvent<HTMLButtonElement>) {
    const dugmeler = Array.from(olay.currentTarget.parentElement?.querySelectorAll("button") ?? []);
    const sira = dugmeler.indexOf(olay.currentTarget);
    const hedef =
      olay.key === "Home"
        ? 0
        : olay.key === "End"
          ? dugmeler.length - 1
          : olay.key === "ArrowRight" || olay.key === "ArrowDown"
            ? (sira + 1) % dugmeler.length
            : olay.key === "ArrowLeft" || olay.key === "ArrowUp"
              ? (sira - 1 + dugmeler.length) % dugmeler.length
              : null;
    if (hedef === null) return;
    olay.preventDefault();
    dugmeler[hedef]?.focus();
  }
  if (yalin) {
    return (
      <div data-ui="grafik" style={{ minWidth: 0, maxWidth: "100%" }}>
        {bos ? (
          <p role="img" aria-label={`${baslik}. ${bosMesaji}`} className="text-notr-600 text-sm">
            {bosMesaji}
          </p>
        ) : (
          children
        )}
      </div>
    );
  }
  return (
    <div
      data-ui="grafik"
      style={{
        minWidth: 0,
        maxWidth: "100%",
        display: kucuk ? "inline-block" : "block",
        verticalAlign: "middle",
      }}
    >
      <p className="text-notr-800 text-sm" style={{ overflowWrap: "anywhere" }}>
        {ozet}
      </p>
      <button
        type="button"
        className={ODAK_SINIFI}
        aria-expanded={tabloAcik}
        aria-controls={kimlik}
        onClick={() => tabloyuAc(!tabloAcik)}
      >
        {tabloAcik ? "Grafik olarak göster" : "Tablo olarak göster"}
      </button>
      <div id={kimlik}>
        {tabloAcik ? (
          <div role="region" aria-label={`${baslik} — veri tablosu`}>
            <Tablo sutunlar={sutunlar} satirlar={satirlar} anahtar={(satir) => satir.anahtar} />
            {satirlar.length === 0 && <BosDurum baslik={bosMesaji} />}
          </div>
        ) : bos ? (
          <>
            <div role="img" aria-label={`${baslik}. ${bosMesaji}`} />
            <BosDurum baslik={bosMesaji} aciklama="Ölçüm girildiğinde grafik burada görünecek." />
          </>
        ) : (
          <>
            {children}
            {aciklama}
            {/* role=img altındaki çocuklar bazı okuyucularda gizlenir; değer düğmeleri dışarıda kalır. */}
            <details>
              <summary className="min-h-dokunma text-notr-800 text-sm">
                Değerleri klavyeyle oku
              </summary>
              <p className="text-notr-600 text-sm">
                Ok tuşlarıyla, Home ve End ile kayıtlar arasında gezin.
              </p>
              <div className="flex flex-wrap gap-2">
                {satirlar.map((satir) => {
                  const metin = `${satir.etiket}: ${basliklar.map((baslikMetni, sira) => `${baslikMetni} ${satir.hucreler[sira] ?? "Ölçülmedi"}`).join("; ")}`;
                  return (
                    <button
                      key={satir.anahtar}
                      type="button"
                      className={ODAK_SINIFI}
                      aria-label={metin}
                      onKeyDown={klavyeGezintisi}
                      onFocus={() => okumayiDegistir(metin)}
                      onClick={() => okumayiDegistir(metin)}
                    >
                      {metin}
                    </button>
                  );
                })}
              </div>
              <p
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="text-notr-800 text-sm"
              >
                {okunan}
              </p>
            </details>
          </>
        )}
      </div>
    </div>
  );
}
